import React, { useCallback, useEffect, useState } from "react";
import { Slider } from "radix-ui";
import { ImgLoader } from "@treegram/loaders-img";
import { FragmentsManager } from "@thatopen/components";
import Card from "@/components/card";
import useViewerContext from "@/services/context/viewer/instance/use-viewer-context.ts";
import ToggleButton from "@/components/buttons/button-toggle";
import usePackDetails from "@/pages/takeoff/layouts/validation/hooks/use-pack-details.ts";
import useProjectMeasureContext from "@/pages/takeoff/context/project-measure/use-project-measure-context";
import FileType from "@/models/enums/file-type";
import { resolveViewerObjectIds } from "@/utils/viewer-object-identity.ts";

interface PackToolbarProps {
    packId: string;
    storeyId: string;
}

const PackToolbar: React.FC<PackToolbarProps> = ({ packId, storeyId }) => {
    const { projectMeasures, storeysFileData, buildingsFileData } = useProjectMeasureContext();

    const { data } = usePackDetails(packId);
    const { viewer, getLoader } = useViewerContext();

    const [sliderValue, setSliderValue] = useState(0);
    const [isContextChecked, setIsContextChecked] = useState(false);

    useEffect(() => {
        if (viewer == null) return;
        const loader = getLoader(ImgLoader);
        if (!loader) return;

        const file = storeysFileData.get(storeyId)?.[0];
        if (!file) return;

        const storey = projectMeasures.getStorey(storeyId);
        const firstFile3D = Array.from(buildingsFileData.values())
            .map(files =>
                files.find(f => f.fileType === FileType.File3D && f.sitePosition != null)
            )
            .find(Boolean);

        const defaultAltimetry = file.sitePosition
            ? (storey?.altimetry ?? 0) - (firstFile3D?.sitePosition?.z ?? 0)
            : storey?.altimetry ?? 0;
        const altimetryToApply = sliderValue * 0.01;
        loader.updateOptions({ altimetry: defaultAltimetry + altimetryToApply });
    }, [viewer, getLoader, sliderValue, storeyId, storeysFileData, buildingsFileData, projectMeasures]);

    const handleContext = useCallback(async (enabled: boolean) => {
        if (data == null || viewer == null) return;
        const { _3D } = data;
        const fragments = viewer.getComponents().get(FragmentsManager);
        const aliases = await resolveViewerObjectIds([_3D], fragments.list.values());
        const viewerIds = aliases.get(_3D) ?? [];
        setIsContextChecked(enabled);

        if (enabled) {
            await viewer.showItems(viewerIds);
        }
        else {
            await viewer.hideItems(viewerIds);
        }
    }, [data, viewer]);

    return (
        <>
            <Card additionalContentClasses='flex flex-row gap-x-0.75 items-center pl-1.5 pr-0.5'
                additionalClasses='h-fit px-1 pointer-events-auto py-2'>
                <Slider.Root
                    className='relative flex items-center w-24 touch-none select-none mr-2'
                    orientation='horizontal' min={-30} max={30} step={1} defaultValue={[0]}
                    value={[sliderValue]}
                    onValueChange={(value) => setSliderValue(value[0])}>
                    <Slider.Track className='relative grow h-1 rounded bg-gray-100'>
                        <Slider.Range className='absolute h-full rounded-full bg-blue-500' />
                    </Slider.Track>
                    <Slider.Thumb className='block size-3 rounded-full bg-blue-300 shadow-lg hover:bg-blue-400' />
                </Slider.Root>

                <>
                    <div className='border-s border-gray-300 h-4' />

                    <ToggleButton toggled={isContextChecked} setIsToggled={(e) => handleContext(e)}
                        variant='ghost' size='2xs'>
                        <span className={isContextChecked ? 'font-semibold' : 'font-base'}>Contexte</span>
                    </ToggleButton>
                </>

            </Card>
        </>
    )
}

export default PackToolbar;
